import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { subscribeWithSelector } from 'zustand/middleware';

/**
 * Onboarding Animation Phases - Clear state machine
 * Each phase must complete before the next begins
 */
export type OnboardingPhase = 
  | 'idle'              // No animation
  | 'overlay'           // Welcome overlay showing
  | 'overlay-complete'  // Overlay done, ready for header
  | 'header'            // Header animating
  | 'header-complete'   // Header done, ready for chat
  | 'chat'              // Chat animating (desktop) or initializing (mobile)
  | 'chat-complete'     // Chat ready, can send welcome message
  | 'welcome-sent'      // Welcome message sent, sequence complete
  | 'complete';         // All done, cleanup

interface OnboardingState {
  // Session flags (runtime-only, not persisted — source of truth is the backend user profile)
  isNewUser: boolean;
  shouldOpenChat: boolean;
  welcomeModalShown: boolean;
  successModalShown: boolean;
  voiceConfigPromptShown: boolean;

  // Animation control flags (persisted)
  useAnimatedIntro: boolean;
  cameFromAnimatedIntro: boolean;

  // Animation state (not persisted - runtime only)
  currentPhase: OnboardingPhase;
  showOverlay: boolean;
  overlayComplete: boolean;
  headerAnimationComplete: boolean;
  chatAnimationComplete: boolean;
  sidebarAnimationComplete: boolean;
  welcomeMessageSent: boolean;
  
  // Captured data for welcome message
  capturedThreadId: string | null;
  chatStoreInitialized: boolean;

  // Actions
  setIsNewUser: (value: boolean) => void;
  setShouldOpenChat: (value: boolean) => void;
  setWelcomeModalShown: (value: boolean) => void;
  setSuccessModalShown: (value: boolean) => void;
  setVoiceConfigPromptShown: (value: boolean) => void;
  setUseAnimatedIntro: (value: boolean) => void;
  setCameFromAnimatedIntro: (value: boolean) => void;
  setShowOverlay: (value: boolean) => void;
  setOverlayComplete: (value: boolean) => void;
  setHeaderAnimationComplete: (value: boolean) => void;
  setChatAnimationComplete: (value: boolean) => void;
  setSidebarAnimationComplete: (value: boolean) => void;
  setWelcomeMessageSent: (value: boolean) => void;
  
  // State machine actions
  setCapturedThreadId: (threadId: string) => void;
  setChatStoreInitialized: (value: boolean) => void;
  advancePhase: () => void;
  resetAnimationState: () => void;
}

export const useOnboardingStore = create<OnboardingState>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // Initial state
        isNewUser: false,
        shouldOpenChat: false,
        welcomeModalShown: false,
        successModalShown: false,
        voiceConfigPromptShown: false,
        useAnimatedIntro: true, // Enable animated intro by default
        cameFromAnimatedIntro: false,
        
        // Runtime state (not persisted)
        currentPhase: 'idle',
        showOverlay: false,
        overlayComplete: false,
        headerAnimationComplete: false,
        chatAnimationComplete: false,
        sidebarAnimationComplete: false,
        welcomeMessageSent: false,
        capturedThreadId: null,
        chatStoreInitialized: false,

        setIsNewUser: (value) => {
          console.log('[OnboardingStore] ✏️ setIsNewUser:', value);
          set({ isNewUser: value });
          console.log('[OnboardingStore] ✅ State updated. Full state:', useOnboardingStore.getState());
        },

        setShouldOpenChat: (value) => {
          console.log('[OnboardingStore] ✏️ setShouldOpenChat:', value);
          set({ shouldOpenChat: value });
          console.log('[OnboardingStore] ✅ State updated. Full state:', useOnboardingStore.getState());
        },

        setWelcomeModalShown: (value) => {
          console.log('[OnboardingStore] ✏️ setWelcomeModalShown:', value);
          set({ welcomeModalShown: value });
          console.log('[OnboardingStore] ✅ State updated. Full state:', useOnboardingStore.getState());
        },

        setSuccessModalShown: (value) => {
          console.log('[OnboardingStore] ✏️ setSuccessModalShown:', value);
          set({ successModalShown: value });
          console.log('[OnboardingStore] ✅ State updated. Full state:', useOnboardingStore.getState());
        },
        
        setVoiceConfigPromptShown: (value) => {
          console.log('[OnboardingStore] ✏️ setVoiceConfigPromptShown:', value);
          set({ voiceConfigPromptShown: value });
          console.log('[OnboardingStore] ✅ State updated. Full state:', useOnboardingStore.getState());
        },

        setUseAnimatedIntro: (value) => {
          console.log('[OnboardingStore] ✏️ setUseAnimatedIntro:', value);
          set({ useAnimatedIntro: value });
          console.log('[OnboardingStore] ✅ State updated. Full state:', useOnboardingStore.getState());
        },

        setCameFromAnimatedIntro: (value) => {
          console.log('[OnboardingStore] ✏️ setCameFromAnimatedIntro:', value);

          // When starting new animation sequence, reset all completion flags
          if (value === true) {
            console.log('[OnboardingStore] 🔄 Starting new animation - resetting to overlay phase');
            set({
              cameFromAnimatedIntro: true,
              useAnimatedIntro: true,
              currentPhase: 'overlay',
              showOverlay: true,
              overlayComplete: false,
              headerAnimationComplete: false,
              chatAnimationComplete: false,
              sidebarAnimationComplete: false,
              welcomeMessageSent: false,
              capturedThreadId: null,
              chatStoreInitialized: false,
            });
            console.log('[OnboardingStore] ✅ State updated. Full state:', useOnboardingStore.getState());
          } else {
            // Just set the flag
            set({ cameFromAnimatedIntro: value });
          }
        },

        setShowOverlay: (value) => {
          console.log('[OnboardingStore] ✏️ setShowOverlay:', value);
          set({ showOverlay: value });
        },

        setOverlayComplete: (value) => {
          console.log('[OnboardingStore] ✏️ setOverlayComplete:', value);
          set({ overlayComplete: value });
          
          if (value) {
            get().advancePhase();
          }
        },

        setHeaderAnimationComplete: (value) => {
          console.log('[OnboardingStore] ✏️ setHeaderAnimationComplete:', value);
          set({ headerAnimationComplete: value });
          
          if (value) {
            get().advancePhase();
          }
        },

        setChatAnimationComplete: (value) => {
          console.log('[OnboardingStore] ✏️ setChatAnimationComplete:', value);
          set({ chatAnimationComplete: value });
          
          if (value) {
            get().advancePhase();
          }
        },

        setSidebarAnimationComplete: (value) => {
          console.log('[OnboardingStore] ✏️ setSidebarAnimationComplete:', value);
          set({ sidebarAnimationComplete: value });
        },

        setWelcomeMessageSent: (value) => {
          console.log('[OnboardingStore] ✏️ setWelcomeMessageSent:', value);
          set({ welcomeMessageSent: value });
          
          if (value) {
            get().advancePhase();
          }
        },
        
        setCapturedThreadId: (threadId) => {
          console.log('[OnboardingStore] 📥 setCapturedThreadId:', threadId);
          set({ capturedThreadId: threadId });
        },
        
        setChatStoreInitialized: (value) => {
          console.log('[OnboardingStore] ✏️ setChatStoreInitialized:', value);
          set({ chatStoreInitialized: value });
          
          if (value) {
            get().advancePhase();
          }
        },

        /**
         * State machine: Advance to next phase based on current state
         * This eliminates race conditions by having clear phase transitions
         * Advances multiple times if needed (for transition phases)
         */
        advancePhase: () => {
          let state = get();
          let { currentPhase, overlayComplete, headerAnimationComplete, chatStoreInitialized, chatAnimationComplete, welcomeMessageSent } = state;
          
          console.log('[OnboardingStore] 🎯 advancePhase called:', { currentPhase, overlayComplete, headerAnimationComplete, chatStoreInitialized, chatAnimationComplete, welcomeMessageSent });
          
          let advanced = false;
          let iterations = 0;
          const MAX_ITERATIONS = 10; // Prevent infinite loops
          
          // Keep advancing until we reach a stable phase
          while (iterations < MAX_ITERATIONS) {
            iterations++;
            let nextPhase: OnboardingPhase = currentPhase;
            
            switch (currentPhase) {
              case 'idle':
                // No animation active
                break;
                
              case 'overlay':
                if (overlayComplete) {
                  nextPhase = 'overlay-complete';
                  console.log('[OnboardingStore] 🎬 Overlay complete → Header can start');
                }
                break;
                
              case 'overlay-complete':
                // Transition phase - immediately advance to header
                nextPhase = 'header';
                console.log('[OnboardingStore] ⏭️ Auto-advancing overlay-complete → header');
                break;
                
              case 'header':
                if (headerAnimationComplete) {
                  nextPhase = 'header-complete';
                  console.log('[OnboardingStore] 🎬 Header complete → Chat can start');
                }
                break;
                
              case 'header-complete':
                // ✅ CRITICAL: Wait for chatStore to initialize BEFORE starting animations
                // This ensures chat content is ready when the panel slides in
                // Without this, the chat panel slides in EMPTY and content "pops in" later
                if (chatStoreInitialized) {
                  nextPhase = 'chat';
                  console.log('[OnboardingStore] ⏭️ ChatStore ready → Advancing to chat phase');
                } else {
                  console.log('[OnboardingStore] ⏸️ Waiting for chatStore initialization before starting animations');
                }
                break;
                
              case 'chat':
                // Animation phase - wait for animation to complete
                // (chatStoreInitialized is guaranteed true here - it's a prerequisite to enter this phase)
                if (chatAnimationComplete) {
                  nextPhase = 'chat-complete';
                  console.log('[OnboardingStore] 🎬 Chat animation complete → Welcome message can send');
                }
                break;
                
              case 'chat-complete':
                // Ready to send welcome message
                // This phase is for SpacePageWrapper to send the message
                if (welcomeMessageSent) {
                  nextPhase = 'welcome-sent';
                  console.log('[OnboardingStore] 🎬 Welcome sent → Sequence complete');
                }
                break;
                
              case 'welcome-sent':
                nextPhase = 'complete';
                // Clear cameFromAnimatedIntro flag after everything is done
                set({ cameFromAnimatedIntro: false });
                console.log('[OnboardingStore] ✅ Animation sequence COMPLETE');
                break;
                
              case 'complete':
                // Fully done
                break;
            }
            
            if (nextPhase !== currentPhase) {
              console.log('[OnboardingStore] ⏭️ Phase transition:', currentPhase, '→', nextPhase);
              currentPhase = nextPhase;
              advanced = true;
              
              // Update state
              set({ currentPhase: nextPhase });
              
              // Emit event for components that need to react to phase changes
              window.dispatchEvent(new CustomEvent('onboarding-phase-change', {
                detail: { from: state.currentPhase, to: nextPhase }
              }));
              
              // Update local state for next iteration
              state = get();
              chatStoreInitialized = state.chatStoreInitialized;
              chatAnimationComplete = state.chatAnimationComplete;
              welcomeMessageSent = state.welcomeMessageSent;
            } else {
              // No more transitions possible
              break;
            }
          }
          
          if (iterations >= MAX_ITERATIONS) {
            console.error('[OnboardingStore] ⚠️ Maximum iterations reached in advancePhase - possible infinite loop');
          }
          
          if (advanced) {
            console.log('[OnboardingStore] ✅ Final phase after advancement:', get().currentPhase);
          }
        },

        resetAnimationState: () => {
          console.log('[OnboardingStore] 🔄 Resetting animation state');
          set({
            currentPhase: 'idle',
            showOverlay: false,
            overlayComplete: false,
            headerAnimationComplete: false,
            chatAnimationComplete: false,
            sidebarAnimationComplete: false,
            welcomeMessageSent: false,
            capturedThreadId: null,
            chatStoreInitialized: false,
            cameFromAnimatedIntro: false,
          });
        },
      }),
      {
        name: 'onboarding-storage',
        storage: createJSONStorage(() => localStorage),
        // Only persist user profile flags, not runtime animation state
        partialize: (state) => ({
          // Only persist the animated intro UI preference
          // All "shown once" tracking flags are backend-backed via userStore.setOnboardingFlag
          useAnimatedIntro: state.useAnimatedIntro,
        }),
      },
    ),
  ),
);

/**
 * Helper to check if welcome message can be sent
 * Clear condition instead of scattered checks
 */
export function canSendWelcomeMessage(): boolean {
  const state = useOnboardingStore.getState();
  return (
    state.currentPhase === 'chat-complete' &&
    state.capturedThreadId !== null &&
    !state.welcomeMessageSent
  );
}

/**
 * Helper to check if resize should be enabled
 * Clear condition instead of scattered checks
 */
export function isResizeEnabled(): boolean {
  const state = useOnboardingStore.getState();
  const animatingPhases = ['overlay', 'header', 'chat'];
  return !animatingPhases.includes(state.currentPhase);
}
